import { API_ORIGIN as BASE_URL } from "../utils/Environment";


//login API

export const login = async (credentials) => {
    try {
        const response = await fetch(`${BASE_URL}/nexus/v1/login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
        });

        const data = await response.json();

        return {
            status: response.status,
            data,
        };

    } catch (error) {
        console.error("Login failed:", error);

        // Detect CORS or Network failure
        if (error instanceof TypeError && error.message === "Failed to fetch") {
            alert("CORS error or network issue. Please check server permissions or your connection.");
        } else {
            alert("Unexpected error during login.");
        }

        return {
            status: 0,
            message: "CORS or network error occurred.",
        };
    }
};

export async function logout() {
    const response = await fetch(`${BASE_URL}/nexus/v1/logout`, {
        method: 'POST',
        credentials: 'include',
    });
    const data = await response.json();

    return {
        status: response.status,
        data,
    };
}

export async function forgotPassword(credential) {
    const response = await fetch(`${BASE_URL}/nexus/v1/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(credential),
    });
    const data = await response.json();

    return {
        status: response.status,
        data,
    };
}

export const fetchCurrentUser = async () => {
    try {
        const response = await fetch(`${BASE_URL}/nexus/v1/users/me`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const contentType = response.headers.get('Content-Type');
        const isJson = contentType && contentType.includes('application/json');

        const data = isJson ? await response.json() : {};

        return {
            status: response.status,
            ...data,
        };
    } catch (error) {
        // Network error or parsing failure
        return {
            status: 0,
            message: error.message || 'Unknown error',
        };
    }
};

export async function updateUserPreference(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not update energy unit preference.');
        error.status = response.status;
        error.errorCode = data.errorCode;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function acceptLicenseAgreement() {
    const response = await fetch(`${BASE_URL}/nexus/v1/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hasAcceptedLicenseAgreement: true }),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not accept the license agreement.');
        error.status = response.status;
        error.errorCode = data.errorCode;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function markNotificationAsViewed(notificationUnid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/notifications/${notificationUnid}/view`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        throw new Error(data.message || 'Could not mark notification as viewed.');
    }

    return {
        data,
        status: response.status,
    };
}

// user management APIs

export async function getUsers({ pageNumber, pageSize, search = "" }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    if (search) {
        queryParams.append("search", search);
    }

    const response = await fetch(`${BASE_URL}/nexus/v1/users?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch users.');
    }
    return {
        data,
        status: response.status
    };
}

export async function createUser(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not create user.');
    }
    return {
        data,
        status: response.status,
    };
}

export async function updateUser(userId, payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not update user.');
    }
    return {
        data,
        status: response.status,
    };
}

export async function deleteUser(userId) {
    const response = await fetch(`${BASE_URL}/nexus/v1/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not delete user.');
    }
    return {
        data,
        status: response.status,
    };
}

export async function requestAuditTrailReport(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/audit-trail-reports`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not request audit trail report.');
    }
    return {
        data,
        status: response.status,
    };
}

// Payments API

export async function getCustomerInvoices({ pageNumber, pageSize, isOverdue }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    if (isOverdue) {
        queryParams.append("isOverdue", "true");
    }

    const response = await fetch(`${BASE_URL}/nexus/v1/invoices?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch customer invoices.');
    }
    return {
        data,
        status: response.status,
    };
}

export async function getAuditTrailReportStatus(reportId) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/audit-trail-reports/${reportId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch audit trail report status.');
    }
    return {
        data,
        status: response.status,
    };
}


//fleets

export async function getAllFleets(payload) {
    const { pageNumber, pageSize, search } = payload;

    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    if (search) {
        queryParams.append("search", search);
    }

    const response = await fetch(`${BASE_URL}/nexus/v1/zones?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleets.');
    }
    return data;
}

export async function getPaginatedFleets({ pageNumber = 1, pageSize = 25, search = "" }) {
  return await getAllFleets({ pageNumber, pageSize, search });
}

export async function createFleet(postdata) {
    try {
        const response = await fetch(`${BASE_URL}/nexus/v1/zones`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(postdata),
        });

        const data = await response.json();

        return {
            status: response.status,
            ...data,
        };
    } catch (error) {
        // Network error or parsing failure
        return {
            status: 0,
            message: error.message || 'Unknown error',
        };
    }
}

export async function getFleet(payload) {
    const unid = payload
    try {
        const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const data = await response.json();

        return {
            status: response.status,
            ...data,
        };
    } catch (error) {
        // Network error or parsing failure
        return {
            status: 0,
            message: error.message || 'Unknown error',
        };
    }
}


export async function updateFleet(payload, postdata) {
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${payload.unid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    return {
        status: response.status,
        ...data,
    };
}

export async function deleteFleet(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${payload.unid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete fleet.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

// reports
export async function createFleetReport(payload, postdata) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/csv-reports`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not create report.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

export async function getFleetReportStatus(payload) {
    const { unid, reportId } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/csv-reports/${reportId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch report status.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

// status check

export async function getFleetStatus(payload) {
    const { unid, pageSize, pageNumber } = payload
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/status-checks?pageNumber=${pageNumber}&pageSize=${pageSize}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleet status.');
    }
    return {
        data,
        status: response.status
    };
}

export async function startFleetStatus(payload) {
    const { unid } = payload
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/status-checks`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleet status.');
    }
    return {
        data,
        status: response.status
    };
}

// status check with job id
export async function getFleetStatusWithJobId(payload) {
    const { unid, statusCheckId, watchedSerialNumbers = [] } = payload;

    const params = new URLSearchParams();
    watchedSerialNumbers.forEach(sn => params.append('watchedSerialNumbers', sn));

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/status-checks/${statusCheckId}?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleet status.');
    }
    return {
        data,
        status: response.status
    };
}


// units
const ZONE_UNIT_LIST_QUERY_PARAMS = [
    'connected',
    'connectionType',
    'controlSource',
    'currentLimit',
    'hasAlerts',
    'maxIndoorTempC',
    'maxSetpointC',
    'minIndoorTempC',
    'minSetpointC',
    'mode',
    'running',
    'scheduleSource',
    'search',
    'sortBy',
    'sortDirection',
];

const ALL_UNIT_LIST_QUERY_PARAMS = [
    'activated',
    'belongsToZone',
    'configured',
    'search',
    'sortBy',
    'sortDirection',
];

const appendListQueryParams = (params, payload, allowedKeys) => {
    allowedKeys.forEach((key) => {
        const value = payload[key];
        if (value === undefined || value === null || value === '') {
            return;
        }
        params.append(key, String(value));
    });
};

export async function getAllUnit(payload) {
    const { pageNumber, pageSize, unid } = payload;

    const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
    appendListQueryParams(params, payload, ZONE_UNIT_LIST_QUERY_PARAMS);

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch units.');
    }
    return {
        data,
        status: response.status
    };
}

export async function getUnit(payload) {
    const { unid, serialNumber } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units/${serialNumber}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch unit.');
    }
    return {
        data,
        status: response.status
    };
}

export async function getUnitDetails(payload) {
    const { serialNumber } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/units/${serialNumber}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch unit.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

export async function addUnit(payload, postdata) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not add unit.');
    }
    return {
        data,
        status: response.status
    };
}


export async function updateUnit(payload, postdata) {
    const { unid, serialNumber } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units/${serialNumber}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not update unit.');
    }
    return {
        data,
        status: response.status
    };
}

export async function deleteUnit(payload) {
    const { unid, serialNumber } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units/${serialNumber}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not delete unit.');
    }
    return {
        data,
        status: response.status
    };
}

// bulk units

export async function addBulkUnits(payload, postdata) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/units/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not add unit.');
    }
    return {
        data,
        status: response.status
    };
}

//properties
export async function getAllProperties(payload) {
    const { pageNumber, pageSize, nameFragment } = payload;

    const params = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
    });

    if (nameFragment) {
        params.append('nameFragment', nameFragment);
    }

    const response = await fetch(`${BASE_URL}/nexus/v1/properties?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not get all properties.');
    }

    return {
        data,
        status: response.status
    };
}

export async function createProperties(postdata) {
    const response = await fetch(`${BASE_URL}/nexus/v1/properties`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete fleet.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

export async function getProperty(payload) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not get property.');
    }
    return {
        data,
        status: response.status
    };
}

export async function updateProperty(payload, postdata) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not update property.');
        error.status = response.status;
        error.errorCode = data.errorCode;
        throw error; // ✅ Throws a proper Error object
    }
    return {
        data,
        status: response.status
    };
}

export async function deleteProperty(payload) {
  const { unid } = payload;
  const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();

  if (!response.ok) {
    
    // Then throw like this:
throw new ApiError(
  data.message || 'Could not delete property.',
  response.code,
  data.errorCode,
  data
);
  }

  return {
    data,
    status: response.status,
  };
}

class ApiError extends Error {
  constructor(message, status, errorCode, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.data = data;
  }
}





// buildings

export async function createBuildings(payload, postdata) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}/buildings`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not create buildings.');
    }
    return {
        data,
        status: response.status
    };
}

export async function getBuilding(payload) {
    const { unid, buildingUnid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}/buildings/${buildingUnid}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not create buildings.');
    }
    return {
        data,
        status: response.status
    };
}

export async function updateBuilding(payload, postdata) {
    const { unid, buildingUnid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}/buildings/${buildingUnid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete fleet.');
        error.status = response.status;
        throw error;
    }
    return {
        data,
        status: response.status
    };
}

export async function deleteBuilding(payload) {
    const { unid, buildingUnid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/properties/${unid}/buildings/${buildingUnid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new ApiError(
  data.message || 'Could not delete building.',
  response.code,
  data.errorCode,
  data
);
        // throw new Error(data.message || 'Could not create buildings.');
    }
    return {
        data,
        status: response.status
    };
}

//unassociated Units

export async function fetchAllUnassociatedUnits(payload) {
    const {
        activated,
        pageNumber,
        pageSize,
        search, // becomes 'search' in the query
        sortBy,
        sortDirection,
    } = payload;

    const params = new URLSearchParams();

    // Always required
    params.append('pageNumber', String(pageNumber));
    params.append('pageSize', String(pageSize));

    // Optional fields
    if (activated !== undefined) {
        params.append('activated', String(activated));
    }
    if (search) {
        params.append('search', search);
    }
    if (sortBy) {
        params.append('sortBy', sortBy);
    }
    if (sortDirection) {
        params.append('sortDirection', sortDirection);
    }

    const response = await fetch(`${BASE_URL}/nexus/v1/unassociated-units?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch unassociated units.');
    }
    return data;
}


export async function fetchUnassociatedUnit(payload) {
    const { serialNumber } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/unassociated-units/${serialNumber}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch unassociated unit.');
    }
    return {
        data,
        status: response.status
    };
}

export async function updateUnassociatedUnit(payload, postdata) {
    const response = await fetch(`${BASE_URL}/nexus/v1/unassociated-units/${payload.serialNumber}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(postdata),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not update unit.');
    }
    return {
        data,
        status: response.status
    };
}

//alarms api
export async function fetchAlertsForFleet(payload){
    const {pageNumber, pageSize, unid, status, search} = payload
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });
    if(status){
        queryParams.append('status', status);
    }
    if(search){
        queryParams.append('search', search);
    }
   try {
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/alerts?${queryParams.toString()}`,{
        method: 'GET',
        credentials: 'include',
        headers:{
            'Content-Type': 'application/json',
        }
    })
    const data = await response.json();

    return {
        status: response.status,
        ...data,
    }
   } catch (error) {
    return { status: 500, error: error.message };
   }
}

//fetch all units including active, inactive and belongstofleet

export async function fetchAllUnits(payload) {
    const { pageNumber, pageSize } = payload;

    const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
    appendListQueryParams(params, payload, ALL_UNIT_LIST_QUERY_PARAMS);

    const response = await fetch(`${BASE_URL}/nexus/v1/units?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch unassociated units.');
    }
    return data;
}

const buildTopEnergyUsersQuery = ({
    timePeriod,
    pageNumber,
    pageSize,
    includeLastYear,
}) => {
    const params = new URLSearchParams();

    params.append('timePeriod', timePeriod);
    params.append('pageNumber', String(pageNumber));
    params.append('pageSize', String(pageSize));
    if (includeLastYear) {
        params.append('includeLastYear', 'true');
    }

    return params.toString();
};

// fetch topUsers API

export async function fetchTopEnergyUsersRequest(payload) {
    const {
        unid,
        timePeriod,
        pageNumber,
        pageSize,
        includeLastYear,
    } = payload;
    const queryString = buildTopEnergyUsersQuery({
        timePeriod,
        pageNumber,
        pageSize,
        includeLastYear,
    });
    const apiVersion = includeLastYear ? 'v2' : 'v1';
    const response = await fetch(`${BASE_URL}/nexus/${apiVersion}/zones/${unid}/top-energy-users?${queryString}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch top energy users.');
    }
    return {
        data,
        status: response.status
    };
}

//control api

export async function fetchHeatingCoolingSetPoints(payload) {
    const {unid} = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/limits`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

     if (!response.ok) {
        throw new Error(data.message || 'Could not fetch heating/cooling set points.');
    }
    return data;
}

export async function saveHeatingCoolingSetPoints(payload) {
    const {
        unid,
        heatUpperLimitC,
        coolLowerLimitC,
        coldAlertThresholdC,
        hotAlertThresholdC
    } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/limits`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            coolLowerLimitC,
            heatUpperLimitC,
            coldAlertThresholdC,
            hotAlertThresholdC,
        }),
    });

    const data = await response.json();

    if (!data?.data) {
        throw new Error(data.message || 'Could not save heating/cooling set points.');
    }
    return data;
}

export async function fetchFleetControlValues(payload) {
    const {unid} = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/control-values`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleet control values.');
    }
    return data;
}

export async function fetchFleetCurrentLimit(payload) {
    const { unid } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/current-limit`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch current limit.');
    }
    return data;
}

export async function saveFleetControlValues(payload) {
    const {unid, controlValues} = payload;
    
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/control-values`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(controlValues),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not save fleet control values.');
    }
    return data;
}

export async function saveFleetCurrentLimit(payload) {
    const { unid, currentLimit } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/current-limit`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentLimit }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not save current limit.');
    }
    return data;
}

export async function fetchFleetSchedule(payload) {
    const { unid } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/schedule`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch fleet schedule.');
    }
    return data;
}

export async function saveFleetSchedule(payload) {
    const { unid, schedule } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/schedule`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(schedule),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not save fleet schedule.');
    }
    return data;
}

export async function deleteFleetSchedule(payload) {
    const { unid } = payload;

    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/schedule`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Could not clear fleet schedule.');
    }
    return data;
}

// Energy Usage Management APIs
export async function fetchFleetElectricRate(payload) {
    const { unid } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/electric-rate`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (response.status === 404) {
        return {
            data: null,
            status: response.status,
        };
    }

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch electric rate.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status
    };
}

export async function fetchElectricRateOptions() {
    const response = await fetch(`${BASE_URL}/nexus/v1/electric-rate-options`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch electric rate options.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status
    };
}

export async function saveFleetElectricRate(payload) {
    const { unid, electricRate } = payload;
    const response = await fetch(`${BASE_URL}/nexus/v1/zones/${unid}/electric-rate`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(electricRate),
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not save fleet electric rate.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status
    };
}

export async function saveGlobalElectricRate(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/electric-rate`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not save global electric rate.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status
    };
}

export async function deleteGlobalElectricRate() {
    const response = await fetch(`${BASE_URL}/nexus/v1/electric-rate`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete global electric rate.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status
    };
}

export async function fetchEnergyUsage(payload) {
    const { unid, includeLastYear } = payload;
    const path = includeLastYear
        ? `/nexus/v2/zones/${unid}/energy-usage?includeLastYear=true`
        : `/nexus/v1/zones/${unid}/energy-usage`;
    const response = await fetch(`${BASE_URL}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Could not fetch energy usage data.');
    }
    return {
        data,
        status: response.status
    };
}

export async function getNotifications({ pageNumber, pageSize }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    const response = await fetch(`${BASE_URL}/nexus/v1/notifications?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch popups.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function getNotification(unid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/notifications/${unid}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch popup.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function createNotification(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/notifications`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not create popup.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function updateNotification(unid, payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/notifications/${unid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'Could not update popup.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function deleteNotification(unid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/notifications/${unid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete popup.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

//gradient admin customer management

export async function getFMSCustomers({ pageNumber, pageSize }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    const response = await fetch(`${BASE_URL}/nexus/v1/customers?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch customers.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function getFMSCustomer(unid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch customer.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function createFMSCustomer(payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not create customer.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function updateFMSCustomer(unid, payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not update customer.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function deleteFMSCustomer(unid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete customer.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function activateFMSCustomer(unid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}/activate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not activate customer.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function getFMSCustomerUsers(unid, { pageNumber, pageSize }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/users?${queryParams.toString()}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch customer users.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function createFMSCustomerUser(unid, payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}/users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not create customer user.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function deleteFMSCustomerUser(unid, userUnid) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}/users/${userUnid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete customer user.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function getFMSCustomerAllowedSerialNumbers(
    unid,
    { pageNumber, pageSize, serialNumberFragment }
) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    if (serialNumberFragment) {
        queryParams.set('serialNumberFragment', serialNumberFragment);
    }

    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/allowed-serial-numbers?${queryParams.toString()}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch allowed serial numbers.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function addFMSCustomerAllowedSerialNumbers(unid, serialNumbers = []) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}/allowed-serial-numbers`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ serialNumbers }),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not add allowed serial numbers.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function removeFMSCustomerAllowedSerialNumber(unid, serialNumber) {
    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/allowed-serial-numbers/${encodeURIComponent(serialNumber)}`,
        {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not remove allowed serial number.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

//api key management (gradient admins, on behalf of a customer)

export async function getFMSCustomerApiKeys(unid, { pageNumber, pageSize }) {
    const queryParams = new URLSearchParams({
        pageNumber,
        pageSize,
    });

    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/api-keys?${queryParams.toString()}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not fetch API keys.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function createFMSCustomerApiKey(unid, payload) {
    const response = await fetch(`${BASE_URL}/nexus/v1/customers/${unid}/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not create API key.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function updateFMSCustomerApiKey(unid, apiKeyUnid, payload) {
    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/api-keys/${apiKeyUnid}`,
        {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not update API key.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}

export async function deleteFMSCustomerApiKey(unid, apiKeyUnid) {
    const response = await fetch(
        `${BASE_URL}/nexus/v1/customers/${unid}/api-keys/${apiKeyUnid}`,
        {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
        const error = new Error(data.message || 'Could not delete API key.');
        error.status = response.status;
        throw error;
    }

    return {
        data,
        status: response.status,
    };
}
